import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Send, MessageCircle, Phone, AlertCircle } from "lucide-react";
import { adminList, fileUrl, markPosterSent, setGuestPhone } from "../lib/api";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

// Builds the WhatsApp text body. Uses plain text — no emoji characters,
// because some older WhatsApp / device versions render them as "?".
function buildMessage(g, origin, lang) {
  const url = `${origin}/poster/${g.id}?lang=${lang}`;
  if (lang === "te") {
    return (
      `ప్రియమైన ${g.name} గారికి,\n\n` +
      `మా చిన్నారి లక్ష్మి అనఘ ధృతి మొదటి పుట్టినరోజు రాజ్యంలో మాతో ఉండినందుకు హృదయపూర్వక ధన్యవాదాలు!\n\n` +
      `మీ ప్రత్యేక రాజ పోస్టర్ ఇక్కడ ఉంది (లింక్ తెరిచి JPG గా డౌన్‌లోడ్ చేసుకోండి):\n${url}\n\n` +
      `మీ హాజరు ఈ రోజును మరువలేని క్షణంగా మార్చింది.\n\nప్రేమతో,\n- రామ్ & ప్రజ్వల`
    );
  }
  return (
    `Dear ${g.name},\n\n` +
    `Thank you for being part of our daughter Lakshmi Anagha Dhruthi's first birthday kingdom!\n\n` +
    `Your personalized royal poster is here (open and tap Download JPG):\n${url}\n\n` +
    `Your presence made this day truly magical.\n\nWith love,\n- Ram & Prajwala`
  );
}

function normalizePhone(p) {
  if (!p) return "";
  // Keep digits; assume +91 if 10 digits with no country code.
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  return digits;
}

function buildWaUrl(g, origin, lang) {
  const phone = normalizePhone(g.phone);
  if (!phone) return null;
  return `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(g, origin, lang))}`;
}

export default function WhatsAppPanel() {
  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unsent | sent | nophone
  const [selected, setSelected] = useState(new Set());
  const [editingPhone, setEditingPhone] = useState({}); // id → string
  const [savingPhone, setSavingPhone] = useState({});
  const [posterLang, setPosterLang] = useState({}); // id → "te" | "en", default "te"
  const [sending, setSending] = useState(false);
  const [sendLog, setSendLog] = useState([]); // {id, ok}

  const langFor = (id) => posterLang[id] || "te";
  const setLangFor = (id, lang) => setPosterLang((p) => ({ ...p, [id]: lang }));

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    try {
      const all = await adminList();
      // Only show guests whose character is ready/approved (they have a poster)
      const visible = all.filter((g) => ["ready", "approved"].includes(g.status));
      setGuests(visible);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 10000);
    return () => clearInterval(tm);
  }, []);

  const filtered = useMemo(() => {
    switch (filter) {
      case "unsent": return guests.filter((g) => g.phone && !g.poster_sent_at);
      case "sent":   return guests.filter((g) => g.poster_sent_at);
      case "nophone": return guests.filter((g) => !g.phone);
      default: return guests;
    }
  }, [guests, filter]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleAll = () => {
    const eligibleIds = filtered.filter((g) => g.phone).map((g) => g.id);
    if (eligibleIds.every((id) => selected.has(id))) {
      setSelected(new Set([...selected].filter((id) => !eligibleIds.includes(id))));
    } else {
      setSelected(new Set([...selected, ...eligibleIds]));
    }
  };

  const savePhone = async (id) => {
    const newVal = editingPhone[id];
    setSavingPhone((s) => ({ ...s, [id]: true }));
    try {
      await setGuestPhone(id, newVal || null);
      setGuests((gs) => gs.map((g) => (g.id === id ? { ...g, phone: newVal || null } : g)));
      setEditingPhone((e) => { const { [id]: _, ...rest } = e; return rest; });
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to save phone");
    } finally {
      setSavingPhone((s) => ({ ...s, [id]: false }));
    }
  };

  const sendOne = async (g, autoMark = true) => {
    const url = buildWaUrl(g, origin, langFor(g.id));
    if (!url) { alert("Add a phone number first."); return; }
    window.open(url, "_blank", "noopener");
    if (autoMark) {
      try {
        await markPosterSent(g.id, true);
        setGuests((gs) => gs.map((x) => (x.id === g.id ? { ...x, poster_sent_at: new Date().toISOString() } : x)));
      } catch { /* ignore */ }
    }
  };

  const sendBulk = async () => {
    const toSend = filtered.filter((g) => selected.has(g.id) && g.phone);
    if (!toSend.length) { alert("Pick at least one guest with a phone."); return; }
    if (!window.confirm(`Open ${toSend.length} WhatsApp tab${toSend.length === 1 ? "" : "s"}? You'll press Send in each.`)) return;
    setSending(true);
    setSendLog([]);
    for (let i = 0; i < toSend.length; i++) {
      const g = toSend[i];
      const url = buildWaUrl(g, origin, langFor(g.id));
      const opened = window.open(url, "_blank", "noopener");
      let ok = !!opened;
      if (ok) {
        try {
          await markPosterSent(g.id, true);
          setGuests((gs) => gs.map((x) => (x.id === g.id ? { ...x, poster_sent_at: new Date().toISOString() } : x)));
        } catch {
          ok = false;
        }
      }
      setSendLog((l) => [...l, { id: g.id, name: g.name, lang: langFor(g.id), ok }]);
      await new Promise((r) => setTimeout(r, 700));
    }
    setSending(false);
    setSelected(new Set());
  };

  const eligibleCount = filtered.filter((g) => g.phone).length;
  const selectedCount = filtered.filter((g) => selected.has(g.id) && g.phone).length;

  return (
    <section
      className="mb-8 glass-deep rounded-3xl p-5 sm:p-7"
      data-testid="whatsapp-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle size={20} className="text-emerald-700" />
        <h2 className="font-display text-xl text-ink">WhatsApp Poster Outreach</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-3xl">
        Pick guests, set their phone numbers, then click <strong>Send to selected</strong>.
        Each selected guest opens in a new WhatsApp Web tab with their personalized poster
        link pre-filled — just press Send. Status updates to &ldquo;Sent&rdquo; automatically.
      </p>

      {/* Filter pills + bulk send */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          ["all", "All"],
          ["unsent", "Pending"],
          ["sent", "Sent"],
          ["nophone", "No phone"],
        ].map(([k, label]) => {
          const count = (() => {
            switch (k) {
              case "unsent": return guests.filter((g) => g.phone && !g.poster_sent_at).length;
              case "sent": return guests.filter((g) => g.poster_sent_at).length;
              case "nophone": return guests.filter((g) => !g.phone).length;
              default: return guests.length;
            }
          })();
          const active = filter === k;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded-full border ${active ? "bg-plum text-blush border-plum" : "bg-white/70 text-ink border-stone-300 hover:bg-white"}`}
              data-testid={`wa-filter-${k}`}
            >
              {label} <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}

        <div className="grow" />

        <button
          onClick={toggleAll}
          className="text-xs px-3 py-1.5 rounded-full border border-stone-300 bg-white/70 hover:bg-white"
          data-testid="wa-toggle-all"
        >
          {selectedCount === eligibleCount && eligibleCount > 0 ? "Unselect all" : "Select all eligible"}
        </button>
        <button
          onClick={sendBulk}
          disabled={sending || selectedCount === 0}
          className="btn-royal text-xs py-1.5 px-4 disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="wa-send-bulk"
        >
          <Send size={13} /> Send to selected ({selectedCount})
        </button>
      </div>

      {/* Guest table */}
      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white/70">
        <table className="min-w-full text-sm" data-testid="wa-guest-table">
          <thead className="bg-stone-50/80 text-ink-soft uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-3 text-left w-12">&nbsp;</th>
              <th className="px-3 py-3 text-left">Guest</th>
              <th className="px-3 py-3 text-left">Role</th>
              <th className="px-3 py-3 text-left">Phone (with country code)</th>
              <th className="px-3 py-3 text-left">Poster lang</th>
              <th className="px-3 py-3 text-left">Status</th>
              <th className="px-3 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-ink-soft text-xs">No guests match this filter.</td>
              </tr>
            )}
            {filtered.map((g) => {
              const phoneShown = (editingPhone[g.id] ?? g.phone ?? "").trim();
              const dirty = editingPhone[g.id] !== undefined && editingPhone[g.id] !== (g.phone || "");
              const eligible = !!g.phone;
              const sent = !!g.poster_sent_at;
              return (
                <tr key={g.id} className="border-t border-stone-200 hover:bg-stone-50/40" data-testid={`wa-row-${g.id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(g.id)}
                      onChange={() => toggle(g.id)}
                      disabled={!eligible}
                      data-testid={`wa-check-${g.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <img
                        src={fileUrl(g.character_path || g.selfie_path)}
                        alt={g.name}
                        className="w-9 h-9 rounded-full object-cover ring-1 ring-[#D4AF37]"
                      />
                      <div>
                        <p className="font-medium text-ink">{g.name}</p>
                        <p className="text-[10px] text-ink-soft">{g.relationship}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-ink-soft text-xs">
                    <span style={TE}>{g.loving_title || g.role_title}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Phone size={12} className="text-stone-400" />
                      <input
                        type="tel"
                        value={phoneShown}
                        onChange={(e) => setEditingPhone((p) => ({ ...p, [g.id]: e.target.value }))}
                        placeholder="+91 98765 43210"
                        className="bg-transparent border border-stone-300 rounded-lg px-2 py-1 text-xs w-40 focus:outline-none focus:border-[#D4AF37]"
                        data-testid={`wa-phone-${g.id}`}
                      />
                      {dirty && (
                        <button
                          onClick={() => savePhone(g.id)}
                          disabled={savingPhone[g.id]}
                          className="text-xs text-emerald-700 hover:underline disabled:opacity-40"
                          data-testid={`wa-phone-save-${g.id}`}
                        >
                          {savingPhone[g.id] ? "Saving…" : "Save"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {/* Per-row language toggle: ?lang= is embedded in WhatsApp URL */}
                    <div
                      className="inline-flex rounded-full text-[10px] overflow-hidden border border-stone-300 bg-white"
                      data-testid={`wa-lang-${g.id}`}
                    >
                      {[
                        ["te", "TE"],
                        ["en", "EN"],
                      ].map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => setLangFor(g.id, k)}
                          className={`px-2.5 py-1 transition-colors ${langFor(g.id) === k ? "bg-plum text-blush" : "text-ink hover:bg-stone-100"}`}
                          data-testid={`wa-lang-${k}-${g.id}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {sent ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                        <CheckCircle2 size={13} /> Sent
                      </span>
                    ) : eligible ? (
                      <span className="text-amber-700 text-xs">Pending</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-stone-500 text-xs">
                        <AlertCircle size={13} /> Add phone
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a
                      href={eligible ? buildWaUrl(g, origin, langFor(g.id)) : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!eligible) { e.preventDefault(); return; }
                        markPosterSent(g.id, true).then(() =>
                          setGuests((gs) => gs.map((x) => (x.id === g.id ? { ...x, poster_sent_at: new Date().toISOString() } : x)))
                        ).catch(() => {});
                      }}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${eligible ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-stone-200 text-stone-400 cursor-not-allowed"}`}
                      data-testid={`wa-send-one-${g.id}`}
                    >
                      <Send size={11} /> WhatsApp ({langFor(g.id).toUpperCase()})
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent send log */}
      {sendLog.length > 0 && (
        <div className="mt-4 text-xs text-ink-soft" data-testid="wa-send-log">
          <p className="font-medium text-ink mb-1">Recent batch:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            {sendLog.map((l) => (
              <li key={l.id} className={l.ok ? "text-emerald-700" : "text-amber-700"}>
                {l.name} ({(l.lang || "te").toUpperCase()}) — {l.ok ? "tab opened, marked Sent" : "blocked by browser; please retry"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
